import { useState } from "react";

import { Button, Icon } from "@stellar/design-system";

import { PausedActionTooltip } from "@/components/PausedActionTooltip";

import { truncatePublicKey } from "@/helpers/coSigningView";

import type { CoSignerRowInfo } from "../utils/coSignerLookup";

interface CoSignerCellProps {
  info: CoSignerRowInfo | null;
  /**
   * Wired for `state === "invited"` only. Clicking the Invited pill simulates
   * the invitee finishing key setup on VIA Wallet, which flips the row to
   * "Pending approval". When omitted (e.g. read-only Financial Controller
   * view), the pill renders as a static chip.
   */
  onInvitedClick?: () => void;
  /**
   * Wired for `state === "pending"` only. Owner clicks Review to open the QR
   * signing modal shipped in Subtask C. When omitted (FC view — or Subtask A
   * scaffolding without C yet), the Review button is hidden.
   */
  onReviewClick?: () => void;
  /**
   * A turn-off request is pending (the co-signing pause). Review stays
   * visible but disabled, with the pause tooltip, until the pause ends.
   */
  isPaused?: boolean;
}

/**
 * Single cell that renders every co-signer state for a row:
 *
 *  - approved: green ✓ + truncated public key + copy icon
 *  - pending:  yellow "PENDING APPROVAL" pill (+ purple Review button, Owner only)
 *  - invited:  gray "INVITED" pill (clickable for Owner, static for FC)
 *  - null:     empty (not a co-signer)
 */
export const CoSignerCell = ({
  info,
  onInvitedClick,
  onReviewClick,
  isPaused = false,
}: CoSignerCellProps) => {
  const [copiedPubKey, setCopyPubKey] = useState<boolean>(false);

  const handleCopyPubKey = (pubKey: string) => {
    navigator.clipboard.writeText(pubKey).then(() => {
      setCopyPubKey(true);
      setTimeout(() => setCopyPubKey(false), 2000);
    });
  };

  if (!info) {
    return <span className="CoSignerCell CoSignerCell--empty" aria-hidden="true" />;
  }

  if (info.state === "approved") {
    const key = info.publicKey ?? "";
    return (
      <div className="CoSignerCell CoSignerCell--approved">
        <span
          className="CoSignerCell__checkIcon"
          aria-label="Approved co-signer"
          style={{ color: "var(--sds-clr-green-11)" }}
        >
          <Icon.Check />
        </span>
        <code className="CoSignerCell__key" title={key}>
          {truncatePublicKey(key)}
        </code>
        {key ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Copy public key"
            className="CoSignerCell__copy"
            onClick={() => handleCopyPubKey(key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCopyPubKey(key);
              }
            }}
          >
            {copiedPubKey ? <Icon.Check /> : <Icon.Copy01 />}
          </span>
        ) : null}
      </div>
    );
  }

  if (info.state === "pending") {
    return (
      <div className="CoSignerCell CoSignerCell--pending">
        <span className="CoSignerCell__pill CoSignerCell__pill--pending">PENDING APPROVAL</span>
        {onReviewClick ? (
          <PausedActionTooltip isPaused={isPaused}>
            <Button variant="primary" size="sm" disabled={isPaused} onClick={onReviewClick}>
              Review
            </Button>
          </PausedActionTooltip>
        ) : null}
      </div>
    );
  }

  // invited
  const pillClass = "CoSignerCell__pill CoSignerCell__pill--invited";
  if (onInvitedClick) {
    return (
      <div className="CoSignerCell CoSignerCell--invited">
        <button
          type="button"
          className={`${pillClass} CoSignerCell__pill--interactive`}
          onClick={onInvitedClick}
          aria-label="INVITED"
          title="Simulate: they finish setting up their co-signing key"
        >
          INVITED
        </button>
      </div>
    );
  }

  return (
    <div className="CoSignerCell CoSignerCell--invited">
      <span className={pillClass}>INVITED</span>
    </div>
  );
};
